import React, { useMemo } from "react";
import { Radio } from "lucide-react";
import {
  getBinaryOdds,
  getMarketTitle,
  getOutcomeDisplayPrice,
  formatChancePct,
  chancePctNumber,
  groupOutcomesBySelection,
} from "../../utils/bondPredictHelpers";

const shortTitle = (title) => {
  const t = (title || "").trim();
  if (t.length <= 42) return t;
  return `${t.slice(0, 40)}…`;
};

/** Derive a single headline probability (0-100) to plot on the tape. */
const tapeProbability = (market) => {
  const { yesPrice } = getBinaryOdds(market);
  const fromBinary = chancePctNumber(yesPrice);
  if (fromBinary != null) return fromBinary;
  const grouped = groupOutcomesBySelection(market?.outcomes || []).filter(
    (g) => g.yes || g.no
  );
  const lead = grouped[0];
  const price = getOutcomeDisplayPrice(lead?.yes || lead?.no);
  return chancePctNumber(price);
};

const tapeProbabilityLabel = (market) => {
  const { yesPrice } = getBinaryOdds(market);
  if (yesPrice != null) return formatChancePct(yesPrice);
  const grouped = groupOutcomesBySelection(market?.outcomes || []).filter(
    (g) => g.yes || g.no
  );
  const lead = grouped[0];
  const price = getOutcomeDisplayPrice(lead?.yes || lead?.no);
  return formatChancePct(price);
};

const TapeItem = ({ market, onSelect, duplicate = false }) => {
  const pct = tapeProbability(market);
  const pctLabel = tapeProbabilityLabel(market);
  const title = shortTitle(getMarketTitle(market));
  const fullTitle = getMarketTitle(market);
  const live = market?.status === "OPEN";
  const tone = pct == null ? "flat" : pct >= 55 ? "up" : pct <= 45 ? "down" : "flat";
  const probabilityLabel = `${pctLabel} YES probability`;

  return (
    <button
      type="button"
      className={`bp-tape-item bp-tape-item-${tone}`}
      onClick={() => onSelect?.(market)}
      title={`${fullTitle} · ${probabilityLabel}`}
      aria-label={`${fullTitle}: ${probabilityLabel}`}
      aria-hidden={duplicate ? "true" : undefined}
      tabIndex={duplicate ? -1 : 0}
    >
      <span className={`bp-tape-pip${live ? " bp-tape-pip-live" : ""}`} aria-hidden />
      <span className="bp-tape-name">{title}</span>
      <span className="bp-tape-side">YES</span>
      <span className="bp-tape-val">{pctLabel}</span>
      <span className="bp-tape-arrow" aria-hidden>
        {tone === "up" ? "▲" : tone === "down" ? "▼" : "•"}
      </span>
    </button>
  );
};

const PredictPulseTape = ({ markets = [], onSelect }) => {
  const items = useMemo(() => {
    const seen = new Set();
    const list = [];
    for (const m of markets) {
      if (!m?._id || seen.has(m._id)) continue;
      seen.add(m._id);
      list.push(m);
      if (list.length >= 18) break;
    }
    return list;
  }, [markets]);
  const openCount = useMemo(() => items.filter((market) => market?.status === "OPEN").length, [items]);

  if (!items.length) return null;

  // Duplicate the strip so the marquee loops seamlessly.
  const loop = [...items, ...items];

  return (
    <div className="bp-tape" role="marquee" aria-label="Live market probabilities">
      <div className="bp-tape-tag">
        <Radio size={11} strokeWidth={2.6} />
        <span>Live tape</span>
        <strong>{openCount || items.length}</strong>
      </div>
      <div className="bp-tape-viewport">
        <div className="bp-tape-track" style={{ "--bp-tape-count": items.length }}>
          {loop.map((market, i) => (
            <TapeItem
              key={`${market._id}-${i}`}
              market={market}
              onSelect={onSelect}
              duplicate={i >= items.length}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default PredictPulseTape;
